import Link from 'next/link';
import React from 'react';
import { TokenIcon } from '@/components/elements/TokenIcon';
import { useRouter } from 'next/navigation';
import styles from './SearchLinkWithPrice.module.scss';
import { generateTokenUrl } from '@/utils/tokenUrls';
import { generateStockUrl } from '@/utils/stocksUrls';

export type SearchToken = {
  name: string;
  symbol: string;
  decimals: number;
  mint: string;
  imageUri: string;
  coingeckoId?: string | null;
  verified: boolean;
  price?: {
    price?: number;
    change?: number;
    usdPrice?: number;
    usdChange?: number;
    currency?: string;
  };
  isStock?: boolean;
};

function formatPrice(price: number | undefined, decimals?: number): string {
  if (typeof price !== 'number') return '-';
  const minMax =
    typeof decimals === 'number' && !isNaN(decimals)
      ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
      : { minimumFractionDigits: 2, maximumFractionDigits: 6 };
  return `$${price.toLocaleString(undefined, minMax)}`;
}



export function SearchLinkWithPrice({ token, index = 0, isStocks }: { token: SearchToken; index?: number, isStocks: boolean }) {
  const router = useRouter();
  const href = isStocks ? generateStockUrl(token) : generateTokenUrl(token);
  return (
    <Link
      href={href}
      className={styles['search-link']}
      style={{ textDecoration: 'none' }}
      prefetch={false}
      onMouseEnter={() => router.prefetch(href)}
    >
      <TokenIcon
        src={token.imageUri || '/tkn_lnd_pg_assets/images/missing_token_icon.svg'}
        alt={token.symbol}
        verified={token.verified}
        className={styles['search-link__img']}
        index={index}
        isSwiper={false}
      />
      <div className={styles['search-link__main']}>
        <div className={styles['search-link__name']}>{token.name}</div>
        <div className={styles['search-link__symbol']}>{token.symbol}</div>
      </div>
      <div className={styles['search-link__price-block']}>
        <div className={styles['search-link__price']}>
          {token.price ? formatPrice(token.price.usdPrice ?? token.price.price, token.decimals) : '-'}
        </div>
        {(() => {
          const change = token.price ? (token.price.usdChange ?? token.price.change) : undefined;
          if (change === undefined) return null;
          let changeClass = styles['search-link__price-neutral'];
          if (change > 0) changeClass = styles['search-link__price-up'];
          else if (change < 0) changeClass = styles['search-link__price-down'];
          return (
            <div className={changeClass}>
              {change > 0 ? ' +' : change < 0 ? ' -' : ' '}
              {Math.abs(change).toFixed(2)}%
            </div>
          );
        })()}
      </div>
    </Link>
  );
}
